import { Axis } from "@visx/axis";
import type { ScaleBand, ScaleLinear, ScaleTime } from "d3-scale";
import type React from "react";
import { useMemo } from "react";
import { getYFormatSettings, type YFormatType } from "../../../utils/charts";
import { getMaxTextWidth, truncateTextToWidth } from "../../../utils/measureText";
import { getAdaptiveAxisTimeFormat } from "../../../utils/timeFormat";

/**
 * Check if a Date object is valid (not Invalid Date)
 */
function isValidDate(date: Date): boolean {
  return date instanceof Date && !Number.isNaN(date.getTime());
}

/**
 * Font size of a category tick label. Exported so the component sizing its
 * label gutter measures with the same size the axis draws with — the two
 * drifting apart is what makes a "fitted" margin clip anyway.
 */
export const CATEGORY_LABEL_FONT_SIZE = 12;

interface AxisBottomProps {
  top: number;
  scale: ScaleTime<number, number>;
  width: number;
}

/**
 * Calculate optimal tick values based on available width and estimated label width
 * Guarantees first and last labels, then evenly spaces ticks in between
 */
export function calculateOptimalTickValues(
  width: number,
  domain: [Date, Date],
  fontSize = 12,
  useCompact = false
): Date[] {
  // Guard against invalid dates - return empty array if dates are invalid
  if (!isValidDate(domain[0]) || !isValidDate(domain[1])) {
    console.warn("ChartAxis: Invalid date in domain, skipping tick calculation");
    return [];
  }

  const startTime = domain[0].getTime();
  const endTime = domain[1].getTime();
  const timeRange = endTime - startTime;

  // Special case: single data point (or very minimal range)
  // Show only one label at the center
  if (timeRange === 0 || timeRange < 1000) {
    // Less than 1 second
    return [domain[0]];
  }

  const timeFormat = getAdaptiveAxisTimeFormat(
    {
      start: domain[0],
      end: domain[1],
    },
    { compact: useCompact }
  );

  // Sample a few dates across the range to estimate label width
  const sampleDates = [domain[0], new Date((domain[0].getTime() + domain[1].getTime()) / 2), domain[1]];

  const sampleLabels = sampleDates.map((d) => timeFormat(d));
  const maxLabelWidth = getMaxTextWidth(sampleLabels, fontSize);

  // Add generous spacing between labels (100% of label width)
  const labelSpacing = maxLabelWidth;
  const totalLabelSpace = maxLabelWidth + labelSpacing;

  // Calculate how many labels can fit
  const maxTicks = Math.floor(width / totalLabelSpace);

  // Constrain to reasonable bounds (min 2, max 8 for readability)
  const targetTicks = Math.max(2, Math.min(8, maxTicks));

  // If we can only fit 2 labels, just show start and end
  if (targetTicks <= 2) {
    return [domain[0], domain[1]];
  }

  // Calculate evenly spaced ticks between start and end, snapped to
  // clean boundaries so x-axis labels land on round times (on the hour
  // for daily+ windows, on the minute for hourly, etc.).
  const ticks: Date[] = [domain[0]];

  // Determine snap granularity from the window span.
  const spanHours = timeRange / 3_600_000;
  let snapMs: number;
  if (spanHours <= 1) {
    snapMs = 60_000; // 1 minute
  } else if (spanHours <= 6) {
    snapMs = 5 * 60_000; // 5 minutes
  } else if (spanHours <= 48) {
    snapMs = 60 * 60_000; // 1 hour
  } else if (spanHours <= 168) {
    snapMs = 6 * 60 * 60_000; // 6 hours
  } else {
    snapMs = 24 * 60 * 60_000; // 1 day
  }

  // Add intermediate ticks snapped to the nearest grid boundary
  for (let i = 1; i < targetTicks - 1; i++) {
    const fraction = i / (targetTicks - 1);
    const rawTime = startTime + timeRange * fraction;
    const snappedTime = Math.round(rawTime / snapMs) * snapMs;
    // Avoid duplicates with adjacent ticks — and stay within domain
    const prevTime = ticks[ticks.length - 1].getTime();
    let candidate = snappedTime > prevTime ? snappedTime : prevTime + snapMs;
    if (candidate >= endTime) break; // don't exceed the domain
    ticks.push(new Date(candidate));
  }

  // Add end tick
  ticks.push(domain[1]);

  return withoutRepeatedLabels(ticks, timeFormat);
}

/**
 * Drop a tick whose label repeats its neighbour's. A multi-day window snaps to 6-hour boundaries but
 * labels by day, which printed "1 Thu, 1 Thu". The end tick is kept; the repeat before it goes instead.
 */
function withoutRepeatedLabels(ticks: Date[], format: (date: Date) => string): Date[] {
  const kept: Date[] = [];
  for (const tick of ticks) {
    const previous = kept[kept.length - 1];
    if (previous && format(previous) === format(tick)) {
      if (tick === ticks[ticks.length - 1]) {
        // The end tick is the domain's endpoint: replace the repeat before it, or keep both when the
        // start is the only other tick.
        if (kept.length > 1) kept[kept.length - 1] = tick;
        else kept.push(tick);
      }
      continue;
    }
    kept.push(tick);
  }
  return kept;
}

const AxisBottom: React.FC<AxisBottomProps> = ({ top, scale, width }) => {
  const domainArray = scale.domain();
  const domain: [Date, Date] = [domainArray[0], domainArray[domainArray.length - 1]];

  // Check if dates are valid before proceeding
  const hasValidDomain = isValidDate(domain[0]) && isValidDate(domain[1]);

  // Use compact format if space is very constrained (< 400px)
  const useCompactFormat = width < 400;

  // Safely get timestamp or use 0 as fallback for memo dependencies
  const startTime = hasValidDomain ? domain[0].getTime() : 0;
  const endTime = hasValidDomain ? domain[1].getTime() : 0;

  const timeFormat = useMemo(() => {
    if (!hasValidDomain) {
      return () => "";
    }
    return getAdaptiveAxisTimeFormat(
      {
        start: domain[0],
        end: domain[1],
      },
      { compact: useCompactFormat }
    );
  }, [startTime, endTime, useCompactFormat, hasValidDomain]);

  // Calculate optimal tick values with guaranteed start/end labels
  const tickValues = useMemo(
    () => calculateOptimalTickValues(width, domain, 12, useCompactFormat),
    [width, startTime, endTime, useCompactFormat]
  );

  // If no valid ticks (due to invalid dates), render nothing
  if (tickValues.length === 0) {
    return null;
  }

  return (
    <Axis
      orientation="bottom"
      top={top}
      scale={scale}
      tickValues={tickValues}
      tickFormat={(value) => {
        const date = value as Date;
        if (!isValidDate(date)) {
          return "";
        }
        return timeFormat(date);
      }}
      stroke="var(--color-border-muted)"
      tickStroke="var(--color-border-muted)"
      tickLabelProps={(_value, index, ticks) => ({
        fill: "var(--color-text-secondary)",
        fontSize: 12,
        textAnchor: index === 0 ? "start" : index === ticks.length - 1 ? "end" : "middle",
      })}
      // Add padding to ensure edge labels don't get cut off
      rangePadding={0}
    />
  );
};

interface AxisLeftProps {
  /** Optional start-aligned label position relative to the axis. */
  labelLeft?: number;
  left: number;
  scale: ScaleLinear<number, number>;
  yFormatter: YFormatType;
  height?: number;
}

/**
 * Calculate optimal number of Y-axis ticks based on available height
 * Ensures labels don't overlap by considering font size and minimum spacing
 */
function calculateOptimalYTickCount(height: number, fontSize = 12): number {
  // Each tick label needs at least fontSize height plus some padding
  const minTickSpacing = fontSize * 2.5; // Label height + comfortable spacing

  // Calculate how many ticks can fit
  const maxTicks = Math.floor(height / minTickSpacing);

  // Constrain to reasonable bounds (min 2, max 6 for readability)
  return Math.max(2, Math.min(6, maxTicks));
}

const Y_TICK_FONT_SIZE = 12;

/** Space for the widest formatted numeric tick plus its gap to the plot. */
export function getNumericAxisGutter(
  scale: ScaleLinear<number, number>,
  yFormatter: YFormatType,
  height?: number
): number {
  const settings = getYFormatSettings(yFormatter);
  const format = settings.tickFormat || settings.format;
  const labels = scale.ticks(height ? calculateOptimalYTickCount(height) : 5).map((value) => String(format(value)));
  return Math.ceil(getMaxTextWidth(labels, Y_TICK_FONT_SIZE, "system-ui")) + 12;
}

const AxisLeft: React.FC<AxisLeftProps> = ({ left, scale, yFormatter, height, labelLeft }) => {
  const formatSettings = getYFormatSettings(yFormatter);

  // Calculate optimal tick count based on available height
  const numTicks = useMemo(() => {
    if (!height || height <= 0) {
      return 5; // Default fallback
    }
    return calculateOptimalYTickCount(height);
  }, [height]);

  return (
    <Axis
      orientation="left"
      left={left}
      scale={scale}
      numTicks={numTicks}
      tickFormat={(value) => {
        const tickFormat = formatSettings.tickFormat || formatSettings.format;
        return tickFormat(value as number);
      }}
      stroke="var(--color-border-muted)"
      tickStroke="var(--color-border-muted)"
      tickLabelProps={(value) => {
        const base = {
          fill: "var(--color-text-secondary)",
          fontSize: Y_TICK_FONT_SIZE,
          textAnchor: labelLeft === undefined ? ("end" as const) : ("start" as const),
          ...(labelLeft === undefined ? {} : { x: labelLeft }),
        };
        // A centered label on a tick near the plot's top or bottom edge
        // extends past the plot (and gets clipped by overflow-hidden
        // wrappers), so anchor edge labels fully inside instead.
        const y = scale(value as number);
        const [r0, r1] = scale.range();
        const plotTop = Math.min(r0, r1);
        const plotBottom = Math.max(r0, r1);
        if (y - plotTop < Y_TICK_FONT_SIZE / 2) {
          return { ...base, verticalAnchor: "start" as const };
        }
        if (plotBottom - y < Y_TICK_FONT_SIZE / 2) {
          return { ...base, verticalAnchor: "end" as const };
        }
        return { ...base, dy: "0.33em" };
      }}
    />
  );
};

interface CategoryAxisBottomProps {
  top: number;
  scale: ScaleBand<string>;
}

/**
 * Categorical bottom axis for non-time-series charts (e.g., bar charts with category labels)
 */
const CategoryAxisBottom: React.FC<CategoryAxisBottomProps> = ({ top, scale }) => {
  return (
    <Axis
      orientation="bottom"
      top={top}
      scale={scale}
      stroke="var(--color-border-muted)"
      tickStroke="var(--color-border-muted)"
      tickLabelProps={() => ({
        fill: "var(--color-text-secondary)",
        fontSize: 12,
        textAnchor: "middle",
        dy: "0.25em",
      })}
    />
  );
};

interface CategoryAxisLeftProps {
  /** Label start relative to the axis; omitted preserves end-aligned ticks. */
  labelLeft?: number;
  left: number;
  scale: ScaleBand<string>;
  /**
   * Space the labels have to draw in, in px. Labels wider than this are
   * truncated with an ellipsis; omit it to let them render at full length.
   *
   * The caller owns this number because only it knows how much of the chart
   * the gutter was allowed to claim — see `CategoryBarChart`'s
   * `MAX_CATEGORY_AXIS_FRACTION`.
   */
  availableWidth?: number;
}

/**
 * Categorical left axis for horizontal bar charts with category labels
 */
const CategoryAxisLeft: React.FC<CategoryAxisLeftProps> = ({ left, scale, availableWidth, labelLeft }) => {
  return (
    <Axis
      orientation="left"
      left={left}
      scale={scale}
      stroke="var(--color-border-muted)"
      tickStroke="var(--color-border-muted)"
      // Truncation is a last resort, not the first move: the caller has already
      // widened the gutter to fit these labels, and only the ones that overran
      // its cap arrive here still too long. The full text stays reachable in the
      // tooltip, which reads `label` off the data point rather than the tick.
      tickFormat={
        availableWidth === undefined
          ? undefined
          : (value) => truncateTextToWidth(String(value), availableWidth, CATEGORY_LABEL_FONT_SIZE)
      }
      tickLabelProps={() => ({
        fill: "var(--color-text-secondary)",
        fontSize: CATEGORY_LABEL_FONT_SIZE,
        textAnchor: labelLeft === undefined ? "end" : "start",
        ...(labelLeft === undefined ? {} : { x: labelLeft }),
        dx: labelLeft === undefined ? "-0.25em" : 0,
        dy: "0.33em",
      })}
    />
  );
};

interface NumericAxisBottomProps {
  top: number;
  scale: ScaleLinear<number, number>;
  yFormatter?: YFormatType;
  width?: number;
}

/**
 * Numeric bottom axis for horizontal bar charts
 */
const NumericAxisBottom: React.FC<NumericAxisBottomProps> = ({ top, scale, yFormatter, width }) => {
  const formatSettings = getYFormatSettings(yFormatter);

  const numTicks = useMemo(() => {
    if (!width || width <= 0) {
      return 5;
    }
    // Similar logic to Y axis but for horizontal space
    const minTickSpacing = 60; // Approximate width needed per tick
    const maxTicks = Math.floor(width / minTickSpacing);
    return Math.max(2, Math.min(8, maxTicks));
  }, [width]);

  return (
    <Axis
      orientation="bottom"
      top={top}
      scale={scale}
      numTicks={numTicks}
      tickFormat={(value) => {
        const tickFormat = formatSettings.tickFormat || formatSettings.format;
        return tickFormat(value as number);
      }}
      stroke="var(--color-border-muted)"
      tickStroke="var(--color-border-muted)"
      tickLabelProps={() => ({
        fill: "var(--color-text-secondary)",
        fontSize: 12,
        textAnchor: "middle",
        dy: "0.25em",
      })}
    />
  );
};

/**
 * ChartAxis
 *
 * Chart axis components for rendering X and Y axes.
 * Provides formatted bottom (time) and left (numeric) axes with customizable tick formatting.
 *
 * Components:
 * - Bottom: Time-based x-axis (for time-series charts)
 * - Left: Numeric y-axis
 * - CategoryBottom: Categorical x-axis (for bar charts with labels)
 * - CategoryLeft: Categorical y-axis (for horizontal bar charts)
 * - NumericBottom: Numeric x-axis (for horizontal bar charts)
 */
export const ChartAxis = {
  Bottom: AxisBottom,
  Left: AxisLeft,
  CategoryBottom: CategoryAxisBottom,
  CategoryLeft: CategoryAxisLeft,
  NumericBottom: NumericAxisBottom,
};

// Export the tick count calculation for use in grid lines
export { calculateOptimalYTickCount };
